import {
  type Identifier,
  useCreateSuggestionContext,
  useTranslate,
} from "ra-core";
import { useFormContext } from "react-hook-form";

import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import { ReferenceInput } from "@/components/admin/reference-input";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useIsMobile } from "@/hooks/use-mobile";
import { ChannelsSection } from "../settings/ChannelsSection";
import type { NoteChannel } from "../types";
import { channelColorStyle } from "./channelColors";

/**
 * The note form's Channel: where the conversation happened. Optional. The
 * first row of the dropdown opens the list Settings has, to add, edit and
 * delete channels without leaving the note. The picker takes the colour of
 * the channel it holds. LCCRM-87.
 */
export const ChannelInput = () => {
  const translate = useTranslate();
  const isMobile = useIsMobile();

  return (
    <ReferenceInput
      source="channel_id"
      reference="note_channels"
      sort={{ field: "name", order: "ASC" }}
      perPage={100}
    >
      <AutocompleteInput
        label={false}
        optionText="name"
        placeholder={translate("resources.notes.channel.placeholder", {
          _: "Channel",
        })}
        helperText={false}
        // Clearing the picker gives "", which is not a null and which Postgres
        // refuses for a bigint column.
        parse={(value) => value || null}
        // The "create" row, used for its place in the list: it opens the
        // channel list rather than making one itself.
        create={<ManageChannels />}
        createLabel="resources.notes.channel.manage"
        createPosition="top"
        triggerStyle={(channel?: NoteChannel) =>
          channelColorStyle(channel?.color)
        }
        className="w-48"
        modal={isMobile}
      />
    </ReferenceInput>
  );
};

// What "Manage channels" opens: the section Settings shows, in a dialog.
const ManageChannels = () => {
  const translate = useTranslate();
  const { onCancel } = useCreateSuggestionContext();
  const { getValues, setValue } = useFormContext();

  // A channel deleted while it is the one picked here would fail the save on
  // its foreign key.
  const handleDeleted = (id: Identifier) => {
    if (getValues("channel_id") === id) {
      setValue("channel_id", null, { shouldDirty: true });
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="sm:max-w-lg" aria-describedby={undefined}>
        <DialogTitle className="sr-only">
          {translate("resources.notes.channel.manage", {
            _: "Manage channels",
          })}
        </DialogTitle>
        {/* Room for the dialog's close button above "New channel". */}
        <div className="pt-4">
          <ChannelsSection onDeleted={handleDeleted} />
        </div>
      </DialogContent>
    </Dialog>
  );
};
